// PopupExpense.jsx
import React, { useState, useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import './popupExpense.css';

import PaperComponent from './PaperComponent';
import HeaderBar from './HeaderBar';
import ProjectSelect from './ProjectSelect';
import TaskSelect from './TaskSelect';
import CategorySelect from './CategorySelect';
import DateAndInvoiceRow from './DateAndInvoiceRow';
import AmountField from './AmountField';
import TaxRow from './TaxRow';
import NotesRow from './NotesRow';
import FileUpload from './FileUpload';
import SubmitButton from './SubmitButton';
import EntitySelect from './EntitySelect';
import FileReupload from './FileReupload';
import InvoiceNumberDialog from './InvoiceNumberDialog';

import {
  formatTyping,
  toFixed2,
  parseNumeric,
  detectUS,
  formatDisplayDate,
  recomputeFromAmount,
  recomputeFromNet,
  recomputeFromTaxAmount,
} from './helpers';

const ALLOWED_TAXES = [0, 5.5, 6, 7, 10, 19, 20, 21];

function PopupExpense({ setOpenPopup, candidates, openPopup, entities, initialEntryMode = 'receipt' }) {
  const isUS = useMemo(() => detectUS(), []);
  const [form, setForm] = useState({
    project: '',
    task: '',
    date: new Date().toISOString().slice(0, 10),
    category: '',
    expenseType: '',
    amount: 0.0,
    taxRate: '0%',
    taxAmount: 0.0,
    netAmount: 0.0,
    invoiceNumber: '',
    notes: '',
    entity: null, // selected entity
  });

  const [amountInput, setAmountInput] = useState("0.00");
  const [netInput, setNetInput] = useState("0.00");
  const [taxAmountInput, setTaxAmountInput] = useState("0.00");
  const [lastEdited, setLastEdited] = useState('amount');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [entryMode, setEntryMode] = useState(initialEntryMode);

  // Sync entry mode when popup opens with a specific initial mode
  useEffect(() => {
    if (openPopup) setEntryMode(initialEntryMode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPopup]);

  // this is the toggle state for the custom tax amount
  const [customTax, setCustomTax] = useState(false);

useEffect(() => {
  if (!file) {
    setPreviewUrl(null);
    return;
  }

  const url = URL.createObjectURL(file);
  setPreviewUrl(url);

  return () => URL.revokeObjectURL(url); // clean up
}, [file]);
  const [loadingFill, setLoadingFill] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [suggestedInvoiceNumber, setSuggestedInvoiceNumber] = useState('');
  const [invoiceEntityPrefix, setInvoiceEntityPrefix] = useState('');
  const [invoiceCurrentCounter, setInvoiceCurrentCounter] = useState(0);
  const [invoiceAvailableGaps, setInvoiceAvailableGaps] = useState([]);
  const [invoiceDialogLoading, setInvoiceDialogLoading] = useState(false);
  function isFloat(value) {
    // Regular expression to match a floating-point number
    const floatRegex = /^[+-]?\d+(\.\d+)?$/;
  
    return floatRegex.test(value);
  }

  useEffect(() => {
    if (openPopup && file && !loadingFill) {
      handleFillForm();
    }
  }, [openPopup, file]);

  const handleEntryModeChange = (mode) => {
    if (mode === entryMode) return;

    setEntryMode(mode);
    setErrors((err) => ({ ...err, file: '' }));

    if (mode === 'manual') {
      setFile(null);
      setLoadingFill(false);
    }
  };

  const [errors, setErrors] = useState({
    entity: '',
    project: '',
    task: '',
    date: '',
    category: '',
    amount: '',
    taxRate: '',
    invoiceNumber: '',
    notes: '',
    file: '',
  });
  const [taxWarning, setTaxWarning] = useState('');

  // Snackbar / toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const openToast = (message, severity = 'success') =>
    setToast({ open: true, message, severity });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  // Normalize project candidates
  const normalized = useMemo(() => {
    const safe = Array.isArray(candidates) ? candidates : [];
    const projects = safe.map((c) => {
      const projectId = c.projectId ?? c.ProjectID ?? c.project ?? c.ProjectName;
      const projectName = c.project ?? c.ProjectName ?? c.projectName ?? String(projectId ?? '');
      const rawTasks = Array.isArray(c.tasks) ? c.tasks : [];
      const tasks = rawTasks
        .map((t) =>
          typeof t === 'string'
            ? { id: t, name: t }
            : { id: t.taskId ?? t.TaskID ?? t.id ?? t.name, name: t.task ?? t.TaskName ?? t.name }
        )
        .filter((t) => t.name && t.name.trim().toLowerCase() !== 'total')
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      return { id: String(projectId ?? ''), name: projectName ?? '', tasks };
    });
    projects.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return projects;
  }, [candidates]);

  const projectOptions = useMemo(
    () => normalized.map((p) => ({ id: p.id, name: p.name })),
    [normalized]
  );

  const tasksMap = useMemo(() => {
    const m = new Map();
    normalized.forEach((p) => m.set(String(p.id), p.tasks));
    return m;
  }, [normalized]);

  const taskOptions = tasksMap.get(String(form.project)) || [];

  const selectedProject = useMemo(
    () => projectOptions.find((p) => String(p.id) === String(form.project)) || null,
    [projectOptions, form.project]
  );
  const selectedTask = useMemo(
    () => taskOptions.find((t) => String(t.id) === String(form.task)) || null,
    [taskOptions, form.task]
  );

  const handleProjectSelect = (_e, option) => {
    setForm((f) => ({ ...f, project: option?.id ?? '', task: '' }));
    setErrors((err) => ({ ...err, project: '', task: '' }));
  };
  const handleTaskSelect = (_e, option) => {
    setForm((f) => ({ ...f, task: option?.id ?? '' }));
    setErrors((err) => ({ ...err, task: '' }));
  };

  const handleEntitySelect = (_e, option) => {
    console.log('Selected entity:', option);
    setForm((f) => ({ ...f, entity: option }));
    setErrors((err) => ({ ...err, entity: '' }));
  };
  useEffect(() => {
    console.log('Form changed:', form);
  }, [form]);

  useEffect(() => {
    if (customTax) {
      // When in custom tax mode, don't auto-recalculate when tax rate changes
      return;
    }
    
    setForm((f) => {
      let next = { ...f };
      if (lastEdited === 'amount') {
        next = { ...next, ...recomputeFromAmount(f.amount, f.taxRate) };
      } else {
        next = { ...next, ...recomputeFromNet(f.netAmount, f.taxRate) };
      }
      setAmountInput(toFixed2(next.amount));
      setNetInput(toFixed2(next.netAmount));
      setTaxAmountInput(toFixed2(next.taxAmount));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.taxRate, customTax]);

  // Typing handlers
  const onAmountTyping = (e) => {
    setLastEdited('amount');
    const rawValue = e.target.value.replace(/,/g, '');
    const formattedValue = rawValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setAmountInput(formattedValue);
    
    if(!isFloat(parseFloat(formattedValue.replace(/,/g, ''))))
        return;
    const numeric = parseFloat(formattedValue.replace(/,/g, ''));
    
    const computed = customTax
      ? recomputeFromTaxAmount(numeric, form.taxAmount, 'amount')
      : recomputeFromAmount(numeric, form.taxRate);
    setNetInput(toFixed2(computed.netAmount));
    setTaxAmountInput(toFixed2(computed.taxAmount));
    setForm((f) => ({ ...f, ...computed }));
    setErrors((err) => ({ ...err, amount: '' }));
  };

  const onNetTyping = (e) => {
    setLastEdited('net');
    const rawValue = e.target.value.replace(/,/g, '');
    const formattedValue = rawValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setNetInput(formattedValue);
    
    if(!isFloat(parseFloat(formattedValue.replace(/,/g, ''))))
        return;
    const numeric = parseFloat(formattedValue.replace(/,/g, ''));
    
    const computed = customTax
      ? recomputeFromTaxAmount(numeric, form.taxAmount, 'net')
      : recomputeFromNet(numeric, form.taxRate);
    setAmountInput(toFixed2(computed.amount));
    setTaxAmountInput(toFixed2(computed.taxAmount));
    setForm((f) => ({ ...f, ...computed }));
  };

  const onTaxAmountTyping = (e) => {
    const rawValue = e.target.value.replace(/,/g, '');
    const formattedValue = rawValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setTaxAmountInput(formattedValue);
    
    if(!isFloat(parseFloat(formattedValue.replace(/,/g, ''))))
        return;
    const numeric = parseFloat(formattedValue.replace(/,/g, ''));
    
    // Recalculate based on what was last edited (amount or net)
    const baseValue = lastEdited === 'amount' ? form.amount : form.netAmount;
    const computed = recomputeFromTaxAmount(baseValue, numeric, lastEdited);
    if (lastEdited === 'amount') {
      setNetInput(toFixed2(computed.netAmount));
    } else {
      setAmountInput(toFixed2(computed.amount));
    }
    setForm((f) => ({ ...f, ...computed }));
  };

  const onAmountBlur = () => setAmountInput(toFixed2(form.amount));
  const onNetBlur = () => setNetInput(toFixed2(form.netAmount));
  const onTaxAmountBlur = () => setTaxAmountInput(toFixed2(form.taxAmount));

  // Simple field updates
  const updateField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((err) => ({ ...err, [name]: '' }));
  };

  const handleCategoryChange = (value) => updateField('category', value);

  // Handle custom tax toggle
  const handleCustomTaxToggle = () => {
    setCustomTax((prev) => !prev);
    if (!customTax) {
      // Switching TO custom tax mode - clear tax rate
      setForm((f) => ({ ...f, taxRate: '' }));
    } else {
      // Switching FROM custom tax mode - reset to 0%
      setForm((f) => ({ ...f, taxRate: '0%' }));
    }
  };

  // Step 1: validate and fetch suggested invoice number, then open dialog
  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = {
      entity: form.entity ? '' : 'Entity is required.',
      project: form.project ? '' : 'Project is required.',
      task: form.task ? '' : 'Task is required.',
      date: form.date ? '' : 'Date is required.',
      category: form.category ? '' : 'Category is required.',
      amount: String(amountInput || '').trim().length > 0 ? '' : 'Amount is required.',
      taxRate: customTax ? '' : (form.taxRate !== '' ? '' : 'Tax rate is required.'),
      notes: form.notes ? '' : 'Notes are required.',
      file: '',
    };

    setErrors(nextErrors);
    const hasError = Object.values(nextErrors).some((msg) => !!msg);
    if (hasError || submitLoading) return;

    try {
      setSubmitLoading(true);
      const resp = await fetch(
        `/api-dev/expenses/nextInvoiceNumber?staffId=${encodeURIComponent(form.entity?.StaffID ?? '')}`
      );
      const data = await resp.json();
      setSuggestedInvoiceNumber(data.suggestedInvoiceNumber || '');
      setInvoiceEntityPrefix(data.entityPrefix || '');
      setInvoiceCurrentCounter(data.currentCounter ?? 0);
      setInvoiceAvailableGaps(data.availableGaps || []);
      setSubmitLoading(false);
      setInvoiceDialogOpen(true);
    } catch (err) {
      console.error('Failed to fetch invoice number:', err);
      openToast('Failed to fetch invoice number preview.', 'error');
      setSubmitLoading(false);
    }
  };

  // Step 2: called from dialog — performs the actual API call
  const handleInvoiceConfirm = async (invoiceChoice) => {
    setInvoiceDialogLoading(true);
    try {
      let actualCategory = form.category;
      if (actualCategory === 'Other Direct Costs') actualCategory = 'Non-billable Expenses';
      else if (actualCategory === 'Reimbursable Expenses') actualCategory = 'Billable Expenses';
      
      const fd = new FormData();
      if (file) fd.append('receipt', file);
      fd.append('staffId', String(form.entity?.StaffID ?? ''));
      fd.append('projectId', String(form.project));
      fd.append('taskId', String(form.task));
      fd.append('date', form.date);
      fd.append('category', actualCategory);
      fd.append('amount', String(form.amount));
      fd.append('netAmount', String(form.netAmount));
      fd.append('taxAmount', String(form.taxAmount));

      let taxRateNum = 0;
      if (customTax) {
        if (form.netAmount > 0) {
          taxRateNum = (form.taxAmount / form.netAmount) * 100;
        }
      } else {
        taxRateNum = form.taxRate && typeof form.taxRate === 'string' && form.taxRate.endsWith('%')
          ? Number(form.taxRate.slice(0, -1))
          : Number(form.taxRate);
      }

      fd.append('taxRate', String(taxRateNum));
      fd.append('notes', form.notes);
      fd.append('invoiceNumber', invoiceChoice);

      const resp = await fetch('/api-dev/expenses/createExpense', {
        method: 'POST',
        body: fd,
      });

      const maybeJson = await resp.json().catch(() => null);

      if (!resp.ok) {
        const msg =
          (maybeJson && (maybeJson.error || maybeJson.message || maybeJson.detail)) ||
          `Failed to create expense (HTTP ${resp.status})`;
        openToast(msg, 'error');
        setInvoiceDialogLoading(false);
        return;
      }

      const { internalInvoiceNumber } = maybeJson;

      openToast('Expense created successfully.', 'success');

      // Trigger download only when a receipt file was attached
      if (file && internalInvoiceNumber) {
        const safeNotes = form.notes.replace(/[\/\\?%*:|"<>]/g, '-');
        const projectName = selectedProject ? selectedProject.name : 'project';
        const safeDate = form.date.replace(/[\/\\?%*:|"<>]/g, '-');
        const filename = `${internalInvoiceNumber}_${safeNotes}_${projectName}_${safeDate}.${file.name.split('.').pop()}`;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(file);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      setInvoiceDialogOpen(false);
      setFile(null);
      setForm({
        project: '',
        task: '',
        date: new Date().toISOString().slice(0, 10),
        category: '',
        expenseType: '',
        amount: 0.0,
        taxRate: '0%',
        taxAmount: 0.0,
        netAmount: 0.0,
        invoiceNumber: '',
        notes: '',
        entity: null,
      });
      setCustomTax(false);
      setInvoiceDialogLoading(false);
      setOpenPopup(false);
    } catch (err) {
      console.error('Create expense failed:', err);
      openToast('Failed to create expense. Please try again.', 'error');
      setInvoiceDialogLoading(false);
    }
  };
  

  // Category normalizer
  const normalizeCategory = (cat) => {
    if (!cat) return '';
    const canon = ['Travel', 'Other Direct Costs', 'Sub-Contracting', 'Reimbursable Expenses'];
    const cleaned = String(cat).trim().toLowerCase();
    const found = canon.find(
      (c) => c.toLowerCase().replace(/\s+/g, ' ') === cleaned.replace(/\s+/g, ' ')
    );
    if (found) return found;
    if (/other\s*direct\s*costs/i.test(cat)) return 'Other Direct Costs';
    if (/reimbursable/i.test(cat)) return 'Reimbursable Expenses';
    if (/sub/i.test(cat)) return 'Sub-Contracting';
    if (/travel/i.test(cat)) return 'Travel';
    return '';
  };

  // Fill from receipt (input_amount = NET base; amount = GROSS)
  const handleFillForm = async () => {
    if (!file) {
      setErrors((err) => ({ ...err, file: 'Receipt file is required.' }));
      return;
    }
    setLoadingFill(true);
    setTaxWarning('');
    
    // Disable custom tax when filling from AI
    setCustomTax(false);
    
    try {
      const formData = new FormData();
      formData.append('receipt', file);
      const resp = await fetch('/api-dev/expenses/fillExpenseFromReceipt', {
        method: 'POST',
        body: formData,
      });
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
      const data = await resp.json();

      const iso = data.date ? String(data.date).slice(0, 10) : form.date;
      const normalizedCat = normalizeCategory(data.category) || form.category;

      // Project / Task — ensure string IDs everywhere
      let newProject = form.project;
      let newTask = form.task;
      let entity = data.staffID;
      console.log("entity returned from receipt:", entity);
      if (data.projectId != null) {
        const pId = String(data.projectId);
        const projectExists = projectOptions.some((p) => String(p.id) === pId);
        if (projectExists) {
          newProject = pId;

          if (data.taskId != null) {
            const tId = String(data.taskId);
            const possibleTasks = tasksMap.get(pId) || [];
            const taskExists = possibleTasks.some((t) => String(t.id) === tId);
            newTask = taskExists ? tId : '';
          } else {
            newTask = '';
          }
        }
      }

      // Tax handling
      let warning = '';
      let displayTaxRate = form.taxRate;
      let calcTaxRateStr = form.taxRate;
      if (data.taxPercent != null && !Number.isNaN(Number(data.taxPercent))) {
        const rateNum = Number(data.taxPercent);
        calcTaxRateStr = `${rateNum}%`;
        if (ALLOWED_TAXES.includes(rateNum)) {
          displayTaxRate = `${rateNum}%`;
        } else {
          displayTaxRate = '';
          warning = `Detected tax ${rateNum}% doesn't match available options. Please choose the correct rate.`;
        }
      }

      // Use NET from input_amount (base 100%)
      const newNet = typeof data.input_amount === 'number' ? data.input_amount : form.netAmount;

      // Compute VAT forward: amount = net + net*rate
      const recomputed = recomputeFromNet(newNet, calcTaxRateStr);
      console.log('Recomputed amounts from receipt:', recomputed);
      const selectedEntity = entities.find(ent => String(ent.StaffID) === String(entity)) || null;
      console.log('Selected entity after receipt fill:', selectedEntity);
      setForm((f) => ({
        ...f,
        date: iso,
        category: normalizedCat,
        project: newProject,
        task: newTask,
        taxRate: displayTaxRate,
        entity: selectedEntity,
        ...recomputed,
      }));

      setNetInput(toFixed2(recomputed.netAmount));
      setAmountInput(toFixed2(recomputed.amount));
      setTaxAmountInput(toFixed2(recomputed.taxAmount));
      setLastEdited('amount');
      setTaxWarning(warning);
    } catch (err) {
      console.error('Failed to fill form:', err);
      openToast('Failed to analyze receipt.', 'error');
    } finally {
      setLoadingFill(false);
    }
  };
  
  const handleClosePopup = () => {
    // clear form, errors, file, etc.
    setForm({
      project: '',
      task: '',
      date: new Date().toISOString().slice(0, 10),
      category: '',
      expenseType: '',
      amount: 0.0,
      taxRate: '0%',
      taxAmount: 0.0,
      netAmount: 0.0,
      invoiceNumber: '',
      notes: '',
      entity: null,
    });
    setAmountInput(toFixed2(0));
    setNetInput(toFixed2(0));
    setTaxAmountInput(toFixed2(0));
    setLastEdited('amount');
    setFile(null);
    setCustomTax(false);
    setLoadingFill(false);
    setSubmitLoading(false);
    setErrors({
      entity: '',
      project: '',
      task: '',
      date: '',
      category: '',
      amount: '',
      taxRate: '',
      invoiceNumber: '',
      notes: '',
      file: '',
    });
    setTaxWarning('');
    setInvoiceDialogOpen(false);
    setSuggestedInvoiceNumber('');
    setInvoiceEntityPrefix('');
    setInvoiceCurrentCounter(0);
    setInvoiceAvailableGaps([]);
    setInvoiceDialogLoading(false);
    setEntryMode('receipt');
    setOpenPopup(false);
  }

  return (
    <>
      <Dialog
        maxWidth={entryMode === 'receipt' && file ? "650px" : "md"}
        fullWidth
        style={{ margin: '70px', }}
        open={openPopup}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <HeaderBar onClose={handleClosePopup} />
        <DialogContent>
          <div className="popup-expense-container">
            <div className="form-section">
              <div className="entry-mode-tabs">
                {/* <button
                  type="button"
                  className={`entry-mode-tab ${entryMode === 'manual' ? 'active' : ''}`}
                  onClick={() => handleEntryModeChange('manual')}
                >
                  Enter Manually
                </button>
                <button
                  type="button"
                  className={`entry-mode-tab ${entryMode === 'receipt' ? 'active' : ''}`}
                  onClick={() => handleEntryModeChange('receipt')}
                >
                  Enter From Receipt
                </button> */}
              </div>

              <form onSubmit={handleSubmit} noValidate>
                {entryMode === 'receipt' && !file && (
                  <FileReupload
                    file={file}
                    onFileChange={(f) => {
                      setFile(f);
                      setErrors((err) => ({ ...err, file: '' }));
                    }}
                    onRemove={() => setFile(null)}
                    errorText={errors.file}
                    firstReceipt
                  />
                )}

                {(entryMode === 'manual' || (entryMode === 'receipt' && file)) && (
                  <>
                <EntitySelect
                  options={entities || []}
                  value={form.entity}
                  onChange={handleEntitySelect}
                  errorText={errors.entity}
                  disabled={false}
                  required
                />

                <ProjectSelect
                  options={projectOptions}
                  value={selectedProject}
                  onChange={handleProjectSelect}
                  errorText={errors.project}
                  required
                />

                <TaskSelect
                  options={taskOptions}
                  value={selectedTask}
                  onChange={handleTaskSelect}
                  disabled={!form.project}
                  errorText={errors.task}
                  projectSelected={!!form.project}
                  required
                />

                <CategorySelect
                  value={form.category}
                  onChange={handleCategoryChange}
                  errorText={errors.category}
                  required
                />

                <DateAndInvoiceRow
                  value={form.date}
                  onChange={(v) => updateField('date', v)}
                  errorText={errors.date}
                  invoiceNumber={form.invoiceNumber}
                  onInvoiceChange={(v) => updateField('invoiceNumber', v)}
                  invoiceErrorText={errors.invoiceNumber}
                  required
                />

                <TaxRow
                  customTax={customTax}
                  onCustomTaxToggle={handleCustomTaxToggle}
                  taxRate={form.taxRate}
                  onTaxRateChange={(v) => {
                    setTaxWarning('');
                    updateField('taxRate', v);
                  }}
                  taxAmountFormatted={taxAmountInput}
                  onTaxAmountTyping={onTaxAmountTyping}
                  onTaxAmountBlur={onTaxAmountBlur}
                  netInput={netInput}
                  onNetTyping={onNetTyping}
                  onNetBlur={onNetBlur}
                  onNetFocus={() => setLastEdited('net')}
                  errorText={errors.taxRate}
                  warningText={taxWarning}
                  value={amountInput}
                  onChange={onAmountTyping}
                  onBlur={onAmountBlur}
                  onFocus={() => setLastEdited('amount')}
                  errorTextAmount={errors.amount}
                  required
                  includeZeroOption
                />

                <NotesRow
                  notes={form.notes}
                  onNotesChange={(v) => updateField('notes', v)}
                  notesErrorText={errors.notes}
                  required
                />

                {entryMode === 'receipt' && (
                  <FileReupload
                    file={file}
                    onFileChange={(f) => {
                      setFile(f);
                      setErrors((err) => ({ ...err, file: '' }));
                    }}
                    onRemove={() => setFile(null)}
                    errorText={errors.file}
                    firstReceipt={false}
                  />
                )}

                <SubmitButton disabled={submitLoading || loadingFill} loading={submitLoading} />
                  </>
                )}
              </form>
            </div>

            {entryMode === 'receipt' && file && (
              <div className="receipt-preview-container">
                {!loadingFill ? (
                  <p className="preview-title">Receipt Preview</p>
                ) : (
                  <p className="preview-title">
                    Analyzing receipt...
                    <CircularProgress size={18} />
                  </p>
                )}
                {file.type.startsWith('image/') ? (
                  <img
                    src={previewUrl}
                    alt="Receipt preview"
                    className="receipt-preview-image"
                  />
                ) : file.type === 'application/pdf' ? (
                  <iframe
                    src={previewUrl}
                    title="Receipt PDF Preview"
                    className="receipt-preview-pdf"
                  />
                ) : (
                  <p className="preview-unsupported">Preview not available for this file type.</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>

      </Dialog>

      <InvoiceNumberDialog
        open={invoiceDialogOpen}
        suggestedInvoiceNumber={suggestedInvoiceNumber}
        entityPrefix={invoiceEntityPrefix}
        currentCounter={invoiceCurrentCounter}
        availableGaps={invoiceAvailableGaps}
        onConfirm={handleInvoiceConfirm}
        onClose={() => setInvoiceDialogOpen(false)}
        loading={invoiceDialogLoading}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PopupExpense;
